import { type FC, type ReactElement, startTransition, useCallback, useMemo, useState } from "react"

import { yupResolver } from "@hookform/resolvers/yup"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import * as yup from "yup"

import { FALLBACK_LANG } from "$/constants"
import { i18n } from "$/i18n"
import { Option } from "$/models/option"
import { getErrorMessages } from "$/utils/get-error-messages"

import {
    Button,
    EButtonVariant,
    ETypographyAlignment,
    ETypographyColor,
    ETypographyFamily,
    ETypographySize,
    ETypographyTag,
    ETypographyWeight,
    Typography
} from "@/3514/components"
import { type TUserState, useIsFakeParticipant, useUser } from "@/3514/hooks"
import { ParticipantLayout } from "@/3514/layouts"
import { EUserRole } from "@/3514/types"
import { buildCssClassName } from "@/3514/utils"
import useChangeProfile from "@/api/mutations/use-change-profile"
import ParticipantWrapper, { EParticipantWrapperSize } from "@/elements/participant-wrapper"
import type { Language } from "@/models/state"
import TextInput from "@/shared/input/TextInput"
import ProfilePhotoHandler from "@/shared/profile-photo-handler/ProfilePhotoHandler"
import Select from "@/shared/select-default/Select"
import { type TEmptyCallback } from "@/shared/types/functions"
import LockSvgGray from "@/svgs/LockSvgGray"
import { getLanguageOptions } from "@/utils/common"

import { WarningMessage } from "../../shared/WarningMessage"

import "./styles.scss"

const schema = yup.object({
    first_name: yup
        .string()
        .required(i18n.t("First name is required"))
        .max(60, i18n.t("participantSide.onboarding.profile.form.input.validation.max", { field: "First Name" })),
    last_name: yup
        .string()
        .required(i18n.t("Last name is required"))
        .max(60, i18n.t("participantSide.onboarding.profile.form.input.validation.max", { field: "Last Name" }))
})

const Profile: FC = (): ReactElement => {
    const { user }: TUserState<EUserRole.Participant> = useUser<EUserRole.Participant>()

    const isFakeParticipant: boolean = useIsFakeParticipant()

    const { t } = useTranslation()
    const submitProfileChanges = useChangeProfile()
    const [isEditing, setIsEditing] = useState(false)
    const [errorsMsg, setErrorsMsg] = useState(null)
    const [profilePhoto, setProfilePhoto] = useState(null)
    const [isBusy, setIsBusy] = useState(false)

    const defaultProfileValues = useMemo(
        () => ({
            first_name: user.firstName,
            last_name: user.lastName,
            language_code: user.languageCode
        }),
        [user.firstName, user.lastName, user.languageCode]
    )

    const {
        register,
        handleSubmit,
        reset,
        setValue,
        formState: { errors },
        getFieldState
    } = useForm({ defaultValues: defaultProfileValues, resolver: yupResolver(schema) })

    const onEditBtnClick: TEmptyCallback = useCallback(
        (): void => void startTransition((): void => setIsEditing(true)),
        [setIsEditing]
    )

    const onProfilePhotoChange = useCallback(
        (photoBase64: string | ArrayBuffer) => {
            setProfilePhoto(photoBase64)
        },
        [setProfilePhoto]
    )

    const onCloseEditing: TEmptyCallback = useCallback((): void => {
        startTransition((): void => setIsEditing(false))
        reset(defaultProfileValues)
    }, [defaultProfileValues, reset])

    const onSubmitEditing = useCallback(
        async values => {
            const submitData = { ...values, photo: profilePhoto }

            setIsBusy(true)

            await submitProfileChanges.mutateAsync(submitData, {
                onSuccess: async () => {
                    setIsEditing(false)

                    const { isDirty: isDirtyLanguageCode } = getFieldState("language_code")

                    if (isDirtyLanguageCode) {
                        window.location.reload()
                    }
                },
                onError: e => void startTransition((): void => setErrorsMsg(getErrorMessages(e))),
                onSettled: () => void startTransition((): void => setIsBusy(false))
            })
        },
        [profilePhoto, getFieldState, submitProfileChanges]
    )

    const languageOptions = useMemo(() => {
        if (user) {
            const options = getLanguageOptions(user.cohortLanguages)
            return isFakeParticipant ? [options?.find(({ value }): boolean => value === "en")] : options
        }
        return []
    }, [user, isFakeParticipant])

    const defaultLanguageIndex = useMemo(() => {
        const index = languageOptions?.findIndex(lang => lang.value === user.languageCode)
        return (
            (index < 0 ? languageOptions?.findIndex(lang => lang.value === FALLBACK_LANG) : index) ||
            languageOptions?.findIndex(l => l.value === user.languageCode)
        )
    }, [languageOptions, user.languageCode])

    const onLanguageSelect = (language: Option) => {
        setValue("language_code", language.value as Language, { shouldDirty: true })
    }

    return (
        <ParticipantLayout>
            <ParticipantWrapper size={EParticipantWrapperSize.Medium} className="pb-[40px] md:pb-[100px] w-full">
                <div className="d-flex justify-content-between mb-5 participant-wrapper-heading">
                    <Typography
                        text={t("My Profile")}
                        tag={ETypographyTag.Heading1}
                        color={ETypographyColor.Black}
                        alignment={ETypographyAlignment.Center}
                        weight={ETypographyWeight.Medium}
                        size={ETypographySize.Heading1}
                        family={ETypographyFamily.DmSerif}
                    />
                    {!isEditing && (
                        <Button
                            variant={EButtonVariant.Outlined}
                            className="profile-edit__btn btn-edit"
                            onClick={onEditBtnClick}
                        >
                            {t("Edit profile")}
                        </Button>
                    )}
                </div>

                <Typography
                    text={t("Your Information")}
                    className="mb-3"
                    color={ETypographyColor.Black}
                    family={ETypographyFamily.DmSerif}
                    weight={ETypographyWeight.Medium}
                    size={ETypographySize.Heading2}
                    tag={ETypographyTag.Heading2}
                />

                <div className="row">
                    <div className="col-12">
                        <TextInput
                            name="first_name"
                            register={register}
                            errors={errors}
                            disabled={!isEditing}
                            className={buildCssClassName("profile-edit__input prefilled")}
                            placeholder={t("My Firstname")}
                            Icon={!isEditing ? <LockSvgGray /> : null}
                        />
                    </div>
                </div>
                <div className="row mb-2">
                    <div className="col-12">
                        <TextInput
                            name="last_name"
                            register={register}
                            errors={errors}
                            disabled={!isEditing}
                            className="profile-edit__input prefilled"
                            placeholder={t("My Lastname")}
                            Icon={!isEditing ? <LockSvgGray /> : null}
                        />
                    </div>
                </div>
                {languageOptions.length > 1 && (
                    <div className="row flex-column mb-4">
                        <div className="col-12 col-md-4 pr-0 mb-3 d-flex align-items-center">
                            <Typography
                                text={t("Preferred Language")}
                                className="m-0"
                                color={ETypographyColor.Black}
                                family={ETypographyFamily.DmSerif}
                                weight={ETypographyWeight.Medium}
                                size={ETypographySize.Heading2}
                                tag={ETypographyTag.Heading2}
                            />
                        </div>
                        <div className="col-sm-12 col-md-5">
                            <Select
                                backgroundWhite
                                optionsList={languageOptions}
                                showDisabledIcon={!isEditing}
                                onSelect={onLanguageSelect}
                                disabled={!isEditing}
                                defaultOptionIndex={defaultLanguageIndex}
                            />
                        </div>
                    </div>
                )}
                <div className="row mb-50">
                    <div className="col-12">
                        <ProfilePhotoHandler
                            isAccessible={isEditing}
                            onChange={onProfilePhotoChange}
                            defaultPhoto={user?.photo}
                            disabled={!isEditing}
                        />
                    </div>
                </div>
                <WarningMessage message={errorsMsg} />
                {isEditing && (
                    <div className="d-flex gap-x-[10px] justify-content-center">
                        <Button variant={EButtonVariant.Outlined} isDisabled={isBusy} onClick={onCloseEditing}>
                            {t("Cancel")}
                        </Button>
                        <Button
                            variant={EButtonVariant.Solid}
                            onClick={handleSubmit(onSubmitEditing)}
                            isDisabled={isBusy}
                        >
                            {t("Save Changes")}
                        </Button>
                    </div>
                )}
            </ParticipantWrapper>
        </ParticipantLayout>
    )
}

export default Profile
